import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import { AnimatedSection, AnimatedText, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  {
    id: 'corporate',
    title: 'Kurumsal Etkinlikler',
    description: 'Gala geceleri, marka lansmanları, basın etkinlikleri, kurumsal törenler. Her kurumsal anı stratejik tasarım ve kusursuz prodüksiyon ile taçlandırıyoruz.',
    image: '/images/events/corporate-day-2.jpeg',
    to: '/services/corporate',
    tags: ['Gala', 'Lansman', 'Zirve', 'VIP'],
  },
  {
    id: 'wedding',
    title: 'Düğün',
    description: 'Her hikayeye özel, zarif ve anlam taşıyan düğün deneyimleri.',
    image: '/images/events/event-8.jpeg',
    to: '/services/wedding',
    tags: ['Tören', 'Dekor'],
  },
  {
    id: 'launch',
    title: 'Açılış & Lansman',
    description: 'Markanızın ilk izlenimini şekillendiren stratejik açılış etkinlikleri.',
    image: '/images/events/corporate-day-1.jpeg',
    to: '/services/launch',
    tags: ['Açılış', 'Basın'],
  },
  {
    id: 'private',
    title: 'Özel Davetler',
    description: 'Kişiye özel kutlamalar ve seçkin özel etkinlikler.',
    image: '/images/events/disco-night.jpeg',
    to: '/services/private',
    tags: ['Kutlama', 'Özel'],
  },
];

const processSteps = [
  { num: '01', title: 'Briefing', desc: 'Projenizi, beklentilerinizi ve vizyonunuzu dinliyoruz.' },
  { num: '02', title: 'Tasarım', desc: 'Mekân, estetik ve deneyim mimarisini belirliyoruz.' },
  { num: '03', title: 'Planlama', desc: 'Lojistik, zamanlama ve tedarikçi koordinasyonunu yönetiyoruz.' },
  { num: '04', title: 'Prodüksiyon', desc: 'Sahne, teknik ekipman ve tüm operasyonu hazırlıyoruz.' },
  { num: '05', title: 'Teslimat', desc: 'Gün geldiğinde her detayın kusursuz işlediğini garanti ediyoruz.' },
];

const faqItems = [
  {
    q: 'Ankara dışında da hizmet veriyor musunuz?',
    a: "Evet, İMER olarak Türkiye'nin tüm illerinde etkinlik planlama ve prodüksiyon hizmeti sunuyoruz. Ankara merkezli olmakla birlikte İstanbul, İzmir, Antalya ve tüm büyükşehirlerde aktif projeler yürütüyoruz.",
  },
  {
    q: 'Bütçe aralığınız nedir?',
    a: 'Her proje kendine özgüdür. Detaylı bir brifing sonrasında size özel bir teklif hazırlıyoruz. Küçük ölçekli özel davetlerden büyük kurumsal etkinliklere kadar geniş bir yelpazeye hizmet veriyoruz.',
  },
  {
    q: 'Etkinlik öncesi kaç ay önceden başlamalıyız?',
    a: 'Büyük kurumsal etkinlikler ve düğünler için en az 3-6 ay öncesinden başlamalıdır. Daha küçük organizasyonlar için 4-8 hafta yeterli olabilir.',
  },
  {
    q: 'Etkinlik günü koordinasyon sağlıyor musunuz?',
    a: 'Evet, etkinlik günü tam kapsamlı prodüksiyon ve koordinasyon hizmetimiz her paketin bir parçasıdır. Deneyimli ekibimiz başından sonuna kadar sahadadır.',
  },
  {
    q: 'Referanslarınızı görebilir miyiz?',
    a: "Portföy sayfamızda tamamladığımız etkinliklere ait görsel ve hikayelerini inceleyebilirsiniz. Detaylı referans görüşmeleri için bizimle iletişime geçin.",
  },
];

export default function Home() {
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, -100]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.2]);

  useEffect(() => {
    axios.get(`${API}/portfolio`).then(res => setPortfolio(res.data.slice(0, 6))).catch(() => {});
    axios.get(`${API}/testimonials`).then(res => setTestimonials(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (testimonials.length > 1) {
      const timer = setInterval(() => {
        setActiveTestimonial(prev => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [testimonials]);

  return (
    <div style={{ backgroundColor: '#070708' }}>

      {/* ─── HERO ──────────────────────────────────────── */}
      <section
        data-testid="home-hero"
        ref={heroRef}
        className="relative overflow-hidden flex items-center justify-start"
        style={{ minHeight: '100vh' }}
      >
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src="https://images.unsplash.com/photo-1617405207340-954e2e19755c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1800"
            alt="İMER Premium Events"
            className="w-full h-full object-cover"
            style={{ transform: 'scale(1.15)', transformOrigin: 'center top' }}
          />
          <div className="absolute inset-0 hero-overlay" />
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse 1000px 600px at 15% 40%, rgba(201,168,76,0.08), transparent 55%)',
            }}
          />
        </motion.div>

        <motion.div
          className="relative z-10 container-luxury py-32"
          style={{ opacity: heroOpacity }}
        >
          <div className="max-w-2xl">
            <motion.span
              className="overline mb-6 block"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7, ease: [0.22,1,0.36,1] }}
            >
              Ankara · Türkiye Geneli
            </motion.span>

            <motion.h1
              className="display-xl mb-6"
              initial={{ opacity: 0, y: 35 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.9, ease: [0.22,1,0.36,1] }}
            >
              Her Kare Bir<br />
              <em style={{ fontStyle: 'italic', color: '#C9A84C' }}>Hikaye Taşır.</em>
            </motion.h1>

            <motion.p
              className="body-text max-w-md mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.75, ease: [0.22,1,0.36,1] }}
            >
              İMER, kurumsal etkinlikleri, düğünleri, lansmanları ve özel kutlamaları;
              estetik, planlama disiplini ve kusursuz icraatla rafine deneyimlere dönüştürür.
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7, ease: [0.22,1,0.36,1] }}
            >
              <Link
                to="/quote"
                className="btn-gold text-xs tracking-[0.14em] uppercase"
                data-testid="home-hero-primary-cta"
              >
                Teklif Al
              </Link>
              <Link
                to="/portfolio"
                className="btn-outline-gold text-xs tracking-[0.14em] uppercase"
                data-testid="home-hero-secondary-cta"
              >
                Portföyü İncele
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll line */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
        >
          <motion.div
            className="w-px mx-auto"
            style={{ height: '56px', background: 'linear-gradient(to bottom, transparent, rgba(201,168,76,0.6), transparent)' }}
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          />
        </motion.div>
      </section>

      {/* ─── SERVICES ──────────────────────────────────── */}
      <section
        data-testid="home-services"
        className="section-py"
        style={{ backgroundColor: '#0B0B0D' }}
      >
        <div className="container-luxury">
          <AnimatedText className="mb-14">
            <span className="overline mb-4 block">Hizmetlerimiz</span>
            <h2 className="display-lg max-w-xl">Tasarım. Planlama. Prodüksiyon.</h2>
          </AnimatedText>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Corporate — featured: full width */}
            <motion.div
              data-testid="service-card-corporate"
              className="md:col-span-2 relative overflow-hidden group cursor-pointer"
              style={{
                height: '400px',
                backgroundColor: '#0E0F12',
                border: '1px solid rgba(201,168,76,0.06)',
              }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.75, ease: [0.22,1,0.36,1] }}
              whileHover={{ borderColor: 'rgba(201,168,76,0.25)' }}
            >
              <img
                src={services[0].image}
                alt={services[0].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 card-overlay" />
              <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12">
                <div className="flex flex-wrap gap-2 mb-4">
                  {services[0].tags.map(tag => (
                    <span
                      key={tag}
                      className="font-body text-[0.65rem] tracking-[0.15em] uppercase px-2.5 py-1"
                      style={{ background: 'rgba(201,168,76,0.2)', color: '#C9A84C' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h3 className="display-md mb-3 group-hover:text-[#C9A84C] transition-colors duration-300">
                  {services[0].title}
                </h3>
                <p className="font-body text-sm text-[rgba(245,240,232,0.7)] max-w-lg mb-5 leading-relaxed">
                  {services[0].description}
                </p>
                <Link
                  to={services[0].to}
                  className="flex items-center gap-2 text-[#C9A84C] font-body text-sm tracking-[0.06em] group/link"
                >
                  <span>Daha Fazla</span>
                  <ArrowRight size={14} className="transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </motion.div>

            {/* Other services */}
            {services.slice(1).map((service, i) => (
              <motion.div
                key={service.id}
                className="relative overflow-hidden group cursor-pointer"
                style={{
                  height: '280px',
                  backgroundColor: '#0E0F12',
                  border: '1px solid rgba(201,168,76,0.06)',
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.75, delay: i * 0.1, ease: [0.22,1,0.36,1] }}
                whileHover={{ borderColor: 'rgba(201,168,76,0.22)' }}
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 card-overlay" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {service.tags.map(tag => (
                      <span key={tag} className="font-body text-[0.6rem] tracking-[0.12em] uppercase px-2 py-0.5" style={{ background: 'rgba(201,168,76,0.18)', color: '#C9A84C' }}>{tag}</span>
                    ))}
                  </div>
                  <h3
                    className="font-display font-medium text-[#F5F0E8] mb-2 group-hover:text-[#C9A84C] transition-colors duration-300"
                    style={{ fontSize: 'clamp(1.25rem, 2.5vw, 1.7rem)' }}
                  >
                    {service.title}
                  </h3>
                  <p className="font-body text-xs text-[rgba(245,240,232,0.6)] mb-4 leading-relaxed">{service.description}</p>
                  <Link to={service.to} className="flex items-center gap-1.5 text-[#C9A84C] font-body text-xs tracking-[0.06em] group/link">
                    Keşfet <ArrowRight size={12} className="transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURED PORTFOLIO ─────────────────────────── */}
      {portfolio.length > 0 && (
        <section data-testid="home-featured-portfolio" className="section-py" style={{ backgroundColor: '#070708' }}>
          <div className="container-luxury">
            <AnimatedText
              className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4"
            >
              <div>
                <span className="overline mb-3 block">Portföy</span>
                <h2 className="display-lg">Çalışmalarımız</h2>
              </div>
              <Link
                to="/portfolio"
                className="btn-outline-gold text-xs tracking-[0.12em] uppercase flex-shrink-0 inline-block"
                data-testid="home-portfolio-view-all"
              >
                Tümünü Gör
              </Link>
            </AnimatedText>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.map(item => (
                <StaggerItem key={item.id}>
                  <motion.div
                    className="relative overflow-hidden group cursor-pointer"
                    style={{
                      height: '300px',
                      backgroundColor: '#0E0F12',
                      border: '1px solid rgba(201,168,76,0.05)',
                    }}
                    whileHover={{ borderColor: 'rgba(201,168,76,0.3)' }}
                  >
                    <img
                      src={`${item.image_url}&w=600`}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 card-overlay opacity-75 group-hover:opacity-90 transition-opacity duration-300" />
                    <div className="absolute inset-0 flex flex-col justify-end p-5">
                      <span className="font-body text-[0.62rem] tracking-[0.18em] uppercase text-[#C9A84C] mb-1.5">
                        {({ corporate: 'Kurumsal', wedding: 'Düğün', launch: 'Lansman', private: 'Özel' })[item.category]}
                      </span>
                      <h3 className="font-display text-lg font-medium text-[#F5F0E8]">{item.title}</h3>
                      <p className="font-body text-xs text-[rgba(245,240,232,0.5)] mt-1">{item.location} · {item.year}</p>
                    </div>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      {/* ─── PHILOSOPHY ──────────────────────────────────── */}
      <section className="section-py" style={{ backgroundColor: '#0B0B0D' }}>
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedText>
              <div
                className="w-10 h-px mx-auto mb-8"
                style={{ background: '#C9A84C' }}
              />
              <blockquote
                className="font-display font-light leading-[1.4] text-[#F5F0E8] mb-8"
                style={{ fontStyle: 'italic', fontSize: 'clamp(1.4rem, 3vw, 2.2rem)' }}
              >
                “Etkinlikler yalnızca organizasyondan ibaret değildir. Doğru ellerde şekillenen bir etkinlik, bir markanın ifadesi, bir anın mimarisi, bir hikayenin başlangıcıdır.”
              </blockquote>
              <div
                className="w-10 h-px mx-auto mb-6"
                style={{ background: '#C9A84C' }}
              />
              <p className="font-body text-xs text-[rgba(245,240,232,0.4)] tracking-[0.18em] uppercase">İMER Events & Weddings Felsefesi</p>
            </AnimatedText>
          </div>
        </div>
      </section>

      {/* ─── PROCESS ───────────────────────────────────── */}
      <section className="section-py" style={{ backgroundColor: '#070708' }}>
        <div className="container-luxury">
          <AnimatedText className="mb-14">
            <span className="overline mb-4 block">Nasıl Çalışıyoruz</span>
            <h2 className="display-lg">İMER Süreci</h2>
          </AnimatedText>

          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {processSteps.map(step => (
              <StaggerItem key={step.num}>
                <div className="flex flex-col">
                  <span
                    className="font-display font-light text-[#C9A84C] leading-none mb-4"
                    style={{ fontSize: '3.5rem', opacity: 0.35 }}
                  >
                    {step.num}
                  </span>
                  <div className="w-8 h-px mb-4" style={{ background: 'rgba(201,168,76,0.35)' }} />
                  <h4 className="font-display text-xl font-medium text-[#F5F0E8] mb-3">{step.title}</h4>
                  <p className="font-body text-sm text-[rgba(245,240,232,0.55)] leading-relaxed">{step.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ─── CORPORATE SPOTLIGHT ──────────────────────────── */}
      <section className="section-py" style={{ backgroundColor: '#0B0B0D' }}>
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection delay={0.1}>
              <div className="relative overflow-hidden" style={{ height: '520px' }}>
                <img
                  src="https://images.unsplash.com/photo-1700781762961-17b4e984a9a9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900"
                  alt="Kurumsal Etkinlik"
                  className="w-full h-full object-cover"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to bottom, transparent 55%, rgba(11,11,13,0.9) 100%)' }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-wrap gap-4">
                  {[['200+', 'Etkinlik'], ['8+', 'Yıl'], ['100%', 'Memnuniyet']].map(([num, label]) => (
                    <div
                      key={label}
                      className="text-center px-5 py-3"
                      style={{ background: 'rgba(7,7,8,0.85)', border: '1px solid rgba(201,168,76,0.18)' }}
                    >
                      <span className="font-display text-2xl font-semibold text-[#C9A84C] block">{num}</span>
                      <span className="font-body text-xs text-[rgba(245,240,232,0.6)] tracking-wide">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.25}>
              <span className="overline mb-5 block">Kurumsal Uzmanlık</span>
              <h2 className="display-lg mb-6">Kurumsal Etkinlikler, İMER Kalitesiyle</h2>
              <p className="body-text mb-6">
                Marka lansmanlarından gala gecelerine, basın etkinliklerinden kurumsal törenlere;
                her kurumsal anı stratejik tasarım ve kusursuz prodüksiyon ile taçlandırıyoruz.
              </p>
              <p className="body-text mb-10">
                C-suite yöneticileri, pazarlama direktörleri ve marka yöneticileri İMER'i tercih ediyor;
                çünkü biz kurumsal kimliğinizi sahnede en doğru biçimde yansıtan stratejik bir partnerız.
              </p>
              <ul className="flex flex-col gap-3 mb-10">
                {[
                  'Kurumsal Gala Geceleri',
                  'Marka Lansmanları',
                  'Basın Etkinlikleri',
                  'Liderlik Zirveleri',
                  'VIP Davet Yönetimi',
                  'Açılış Törenleri',
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 font-body text-sm text-[rgba(245,240,232,0.75)]">
                    <CheckCircle size={14} className="text-[#C9A84C] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/services/corporate" className="btn-gold text-xs tracking-[0.12em] uppercase inline-block">
                Kurumsal Hizmetlerimiz
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ──────────────────────────────── */}
      {testimonials.length > 0 && (
        <section className="section-py" style={{ backgroundColor: '#070708' }}>
          <div className="container-luxury">
            <AnimatedText className="text-center mb-14">
              <span className="overline mb-4 block">Müşteri Görüşleri</span>
              <h2 className="display-lg">Ne Diyorlar</h2>
            </AnimatedText>

            <div className="max-w-3xl mx-auto">
              <div className="relative" style={{ minHeight: '220px' }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTestimonial}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, ease: [0.22,1,0.36,1] }}
                    className="text-center"
                  >
                    <span
                      className="font-display text-7xl text-[#C9A84C] leading-none block mb-2"
                      style={{ opacity: 0.25 }}
                    >
                      “
                    </span>
                    <p
                      className="font-display font-light text-[#F5F0E8] leading-relaxed mb-8"
                      style={{ fontStyle: 'italic', fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)' }}
                    >
                      {testimonials[activeTestimonial]?.text}
                    </p>
                    <div className="flex flex-col items-center gap-1">
                      <span className="font-body text-sm font-medium text-[#C9A84C]">{testimonials[activeTestimonial]?.author}</span>
                      <span className="font-body text-xs text-[rgba(245,240,232,0.45)]">{ testimonials[activeTestimonial]?.role}</span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="flex justify-center gap-2 mt-10">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    style={{
                      width: i === activeTestimonial ? '32px' : '8px',
                      height: '3px',
                      backgroundColor: i === activeTestimonial ? '#C9A84C' : 'rgba(201,168,76,0.2)',
                      borderRadius: '2px',
                      transition: 'all 0.3s ease',
                    }}
                    aria-label={`Referans ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── FAQ ───────────────────────────────────────── */}
      <section className="section-py" style={{ backgroundColor: '#0B0B0D' }}>
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedText>
              <span className="overline mb-4 block">Sık Sorulanlar</span>
              <h2 className="display-lg mb-6">Sorularınızı Yanıtlıyoruz</h2>
              <p className="body-text mb-8">
                En sık aldığımız sorulara cevap veriyoruz.
                Farklı bir sorunuz varsa bize ulaşın.
              </p>
              <Link to="/contact" className="btn-outline-gold text-xs tracking-[0.12em] uppercase inline-block">
                İletişime Geç
              </Link>
            </AnimatedText>

            <AnimatedSection delay={0.2}>
              <Accordion type="single" collapsible className="space-y-2">
                {faqItems.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="rounded-none px-0"
                    style={{ borderBottom: '1px solid rgba(201,168,76,0.10)' }}
                  >
                    <AccordionTrigger className="font-body text-sm font-medium text-[#F5F0E8] hover:text-[#C9A84C] py-5 hover:no-underline text-left">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="font-body text-sm text-[rgba(245,240,232,0.62)] pb-5 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ──────────────────────────────────── */}
      <section className="section-py relative overflow-hidden" style={{ backgroundColor: '#070708' }}>
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 1000px 600px at 50% 50%, rgba(201,168,76,0.05), transparent 70%)' }}
        />
        <div className="container-luxury relative z-10">
          <div className="max-w-xl mx-auto text-center">
            <AnimatedText>
              <span className="overline mb-6 block">Hayalinizdeki Etkinlik</span>
              <h2 className="display-lg mb-6">Birlikte Çalışalım</h2>
              <p className="body-text mb-10">
                Ankara'dan yola çıkarak Türkiye'nin her köşesine ulaşıyoruz.
                Projenizi birlikte planlayalım.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/quote" className="btn-gold text-xs tracking-[0.14em] uppercase">Teklif Al</Link>
                <Link to="/contact" className="btn-outline-gold text-xs tracking-[0.14em] uppercase">İletişime Geç</Link>
              </div>
            </AnimatedText>
          </div>
        </div>
      </section>
    </div>
  );
}
